import { log } from '@/log'
import { PublishDeveloperReportEvent } from '@/usecases/event-bus-for-third-party/event'

interface DeveloperReportThrottleDependency {
  readonly getNow: () => number
}

const WINDOW_SECONDS = 10
// tslint:disable-next-line:no-magic-numbers
const WINDOW_MS = WINDOW_SECONDS * 1000

/**
 * 窓内(直近 WINDOW_MS)に送信する同一シグネチャ(reportSource + level + message)のレポートの上限。
 */
const MAX_SEND_COUNT_PER_SIGNATURE = 3

/**
 * 窓内(直近 WINDOW_MS)に送信するレポート全体の上限。
 * message が毎回異なる形の暴走に備える。持続的な暴走時に平均 3レポート/秒 まで許す判断。
 */
const MAX_SEND_COUNT_TOTAL = 30

/**
 * 上限に達した最後の送信の metaMessages に付けるマーク。
 * 「実際に超過が起きた」という断言ではなく「上限に達したので、以降は窓が空くまで来ても送らない」
 * という宣言。送ることが確定したレポートに乗せるため、マーク自体が抑制で失われることはない。
 */
const SAME_CONTENT_LIMIT_MARK =
  `Reached the limit of ${MAX_SEND_COUNT_PER_SIGNATURE} reports with the same content ` +
  `per ${WINDOW_SECONDS}s window. Further reports with this content will be suppressed.`
const TOTAL_LIMIT_MARK =
  `Reached the limit of ${MAX_SEND_COUNT_TOTAL} reports per ${WINDOW_SECONDS}s window. ` +
  `Further reports of any content will be suppressed.`

/**
 * 実際にサーバへ送信したレポートの記録。
 *
 * 判定はすべて「窓内に何を送ったか」から導出し、送信しなかったイベントは一切記録しない。これにより
 * - 全体上限で落ちたイベントが同一内容の送信カウントを進めてしまうことがない
 * - 記録件数が構造上 MAX_SEND_COUNT_TOTAL を超えず、メモリが有界になる
 */
interface SentRecord {
  readonly signature: string
  readonly sentAt: number
}

const defaultDependency = (): DeveloperReportThrottleDependency => ({
  getNow: () => Date.now(),
})

/**
 * 開発者レポート(publish-developer-reports イベント)の送信量を抑制するフィルタ。
 *
 * atone のようなサードパーティ起点のコールバックは呼び出し回数を front 側で制御できず、
 * サードパーティ内部のエラーループによって同一内容のレポートが瞬間的に数千件 POST された実績がある。
 * そのため直近 WINDOW_MS の sliding window で
 * (1) 同一内容は MAX_SEND_COUNT_PER_SIGNATURE 件まで
 * (2) 全体で MAX_SEND_COUNT_TOTAL 件まで
 * に抑える。人間の操作速度では発動せず、マシン速度の暴走だけに効くことを意図した閾値。
 * @see https://github.com/Everforth/growthpalette-front/issues/9083
 *
 * 抑制の単位はレポート1件であり、ロギング API へのリクエスト1本ではない。1回の publish に複数
 * レポートを載せられるため、件数の上限はリクエスト数の上限と厳密には一致しない。それでも件数で
 * 制御するのは、抑えたいのが「同一内容が機械速度で繰り返される」という内容単位の現象で、どの
 * レポートを落とすかの判断がリクエスト境界では行えないため。リクエスト数そのものを絞る必要が
 * 出た場合は、この層ではなく送信側に別の制御を置く。
 *
 * いずれの上限も、枠を使い切る最後の送信にマークを付けて送る。時間窓の状態は client しか
 * 知らずサーバ側から窓内件数を再構成できないため、「このクライアントがいつ抑制モードに
 * 入ったか」をサーバへ届いたログだけで判別できるようにする意図。
 *
 * LoggingService 側ではなくこの層に置くのは、LoggingService へ渡る message には送信時刻が
 * 埋め込み済み(handle-publish-developer-reports.ts の convertToLoggingAPIFormat)で、
 * そこでは同一内容の判定ができなくなるため。
 */
export class DeveloperReportThrottle {
  private sentLog: SentRecord[] = []

  constructor(
    private readonly dependency: DeveloperReportThrottleDependency = defaultDependency()
  ) {}

  filter(events: PublishDeveloperReportEvent[]): PublishDeveloperReportEvent[] {
    return events
      .map(e => this.admit(e))
      .filter((e): e is PublishDeveloperReportEvent => e !== null)
  }

  /**
   * event を送るか送らないか(null)を判定して送るものを sentLog に記録し、
   * この送信で上限に達する場合はマークを付けて返す。
   */
  private admit(event: PublishDeveloperReportEvent): PublishDeveloperReportEvent | null {
    const now = this.dependency.getNow()
    this.sentLog = this.sentLog.filter(r => now - r.sentAt < WINDOW_MS)

    if (this.sentLog.length >= MAX_SEND_COUNT_TOTAL) {
      log.warn({ rateLimitedDeveloperReport: event })

      return null
    }

    const signature = signatureOf(event)
    const sameSignatureCount = this.sentLog.filter(r => r.signature === signature).length

    if (sameSignatureCount >= MAX_SEND_COUNT_PER_SIGNATURE) {
      return null
    }

    this.sentLog.push({ signature, sentAt: now })

    const marks = [
      ...(sameSignatureCount + 1 === MAX_SEND_COUNT_PER_SIGNATURE ? [SAME_CONTENT_LIMIT_MARK] : []),
      ...(this.sentLog.length === MAX_SEND_COUNT_TOTAL ? [TOTAL_LIMIT_MARK] : []),
    ]

    if (marks.length === 0) {
      return event
    }

    log.warn({ suppressionStartedForDeveloperReports: marks })

    return {
      ...event,
      metaMessages: [...(event.metaMessages || []), ...marks],
    }
  }
}

function signatureOf(event: PublishDeveloperReportEvent): string {
  return `${event.reportSource}:${event.level}:${stringifyMessage(event.message)}`
}

function stringifyMessage(message: string | object): string {
  if (typeof message === 'string') {
    return message
  }

  try {
    return JSON.stringify(message)
  } catch (error) {
    // 循環参照などで直列化できない message は同一内容の判定を諦めて一律に同一扱いする
    return '[unserializable message]'
  }
}
